import { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'

import { SUI, Dropdown as SUIDropdown } from 'shared/ui'
import { emptyArr, getLength } from 'util/arrays'
import { propFunc } from 'util/functions'

import RecipientItem from './RecipientItem'

const RecipientDropdown = ({ sui, ...rest }) => {
  if (sui) return <LegacySUIDropdown {...rest} />
  return <InternalDesignSystemDropdown {...rest} />
}

const InternalDesignSystemDropdown = ({
  className,
  disabled,
  isSearching,
  lastFilteredSize,
  onClick,
  onClose,
  onBlur,
  recipients,
  trigger,
}) => {
  const MenuOverlay = useCallback(
    () => {
      if (!recipients || lastFilteredSize === 0) {
        return null
      }
      return (
        <Dropdown.Menu>
          {recipients.map(r => (
            <Dropdown.Menu.Item key={r.id} itemKey={r.id} item={r}>
              <RecipientItem recipient={r} />
            </Dropdown.Menu.Item>
          ))}
        </Dropdown.Menu>
      )
    },
    [recipients, lastFilteredSize]
  )

  const handleSelect = useCallback(
    (_, recipient) => {
      onClick(recipient.id)
    },
    [onClick]
  )

  const handleVisibilityChange = useCallback(
    v => {
      if (!v) {
        if (onBlur) onBlur()
        if (onClose) onClose()
      }
    },
    [onBlur, onClose]
  )

  const visible = !!(
    !disabled &&
    (getLength(recipients) > 0 || (isSearching && lastFilteredSize > 0))
  )

  return (
    <Dropdown
      className={className}
      strategy="fixed"
      portal="#overlays"
      overlay={<MenuOverlay />}
      shouldUnmountOverlay
      tabIndex="0"
      onVisibleChange={handleVisibilityChange}
      onSelect={handleSelect}
      visible={visible}
    >
      <div>{trigger}</div>
    </Dropdown>
  )
}

const LegacySUIDropdown = ({
  className,
  disabled,
  isSearching,
  onClick,
  onClose,
  onBlur,
  recipients,
  trigger,
  upward,
}) => {
  const suiOptions = useMemo(
    () => {
      if (!isSearching) {
        return recipients.filter(e => !!e).map(r => ({
          key: r.id,
          content: <RecipientItem recipient={r} />,
          onClick: propFunc(onClick, r.id),
          value: r.id,
        }))
      }

      return emptyArr
    },
    [isSearching, recipients, onClick]
  )

  return (
    <SUI>
      <SUIDropdown
        className={className}
        icon={null}
        onBlur={onBlur}
        onClose={onClose}
        open={!disabled}
        openOnFocus={false}
        options={suiOptions}
        scrolling
        trigger={trigger}
        upward={upward}
        tabIndex="-1"
      />
    </SUI>
  )
}

export default styled(RecipientDropdown)`
  &&& {
    width: ${props => (props.sui ? '100%' : 'auto')};
    align-items: center;
    color: ${props => props.theme.color.monochrome.black};
    cursor: pointer;
    display: inline-flex;
    margin-left: ${props => (props.sui ? '' : '4px')};

    .dropdown {
      &.icon {
        /* SUI override for a bigger caret thats closer to icon */
        font-size: ${props => props.theme.fontSize.small} !important;
        margin-left: ${props => props.theme.spacing['4']} !important;
      }
    }

    & > .menu.menu.menu {
      max-width: 350px;
      min-width: 0 !important;
    }
  }
`
